import { inject, Injectable } from "@angular/core";
import { UnitsManager } from "./units-manager";
import { catchError, map, Observable, of, share, tap, throwError } from "rxjs";
import { Unit } from "../../shared/models/units/unit";
import { User } from "../../shared/models/users/user";
import { UnitImportedViewModel } from "../../installations/view-models/unit_imported_view_model";
import {UnitCallEventsFilters} from "../models/unit-call-events-filters";
import {CallEvent} from "../models/call-event";
import {UnitDashboard} from "../models/unit-dashboard";
import {CallApiService} from "../../shared/services/call-api.service";

@Injectable()
export class ApiUnitsManager extends UnitsManager {
  private callApi = inject(CallApiService);
  private _unitRetrieval$: Observable<Unit> | null = null;
  private _unitDashboardRetrieval$: Observable<UnitDashboard> | null = null;
  private _unitUsersRetrieval$: Observable<User[]> | null = null;


  override getUnits(): Observable<Unit[]> {
    return this.get<Unit[]>("/units");
  }

  override getUnitDashboard(installation_id: string, unit_id: string): Observable<UnitDashboard> {
    if (!installation_id) {
      return throwError(() => new Error("Invalid installation id"));
    }

    if (!unit_id) {
      return throwError(() => new Error("Invalid unit id"));
    }

    if (!this._unitDashboardRetrieval$) {
      this._unitDashboardRetrieval$ = this.get<UnitDashboard>(
          `/installations/${installation_id}/units/${unit_id}/dashboard`
      ).pipe(
          share(),
          tap(() => (this._unitDashboardRetrieval$ = null))
      );
    }

    return this._unitDashboardRetrieval$;
  }

  override getUnitDetails(installation_id: string, unit_id: string): Observable<Unit> {
    if (!installation_id) {
      return throwError(() => new Error("Invalid installation id"));
    }

    if (!unit_id) {
      return throwError(() => new Error("Invalid unit id"));
    }

    if (!this._unitRetrieval$) {
      this._unitRetrieval$ = this.get<Unit>(
        `/installations/${installation_id}/units/${unit_id}`
      ).pipe(
        share(),
        tap(() => (this._unitRetrieval$ = null))
      );
    }

    return this._unitRetrieval$;
  }

  getUnitUsers(installation_id: string, unit_id: string): Observable<User[]> {
    if (!installation_id) {
      return throwError(() => new Error("Invalid installation id"));
    }

    if (!unit_id) {
      return throwError(() => new Error("Invalid unit id"));
    }

    if (!this._unitUsersRetrieval$) {
      this._unitUsersRetrieval$ = this.get<User[]>(
        `/installations/${installation_id}/units/${unit_id}/users`
      ).pipe(
        share(),
        tap(() => (this._unitUsersRetrieval$ = null))
      );
    }

    return this._unitUsersRetrieval$;
  }

  getUnitCallEvents(installation_id: string, unit_id: string, pagination?: any, filters?: UnitCallEventsFilters): Observable<CallEvent[]> {
    if (!installation_id) {
      return throwError(() => new Error("Invalid installation id"));
    }

    if (!unit_id) {
      return throwError(() => new Error("Invalid unit id"));
    }

    const queryParams = new URLSearchParams();

    if (pagination?.index !== undefined && pagination.index !== null) {
      queryParams.set('page_index', pagination.index.toString());
    }
    if (pagination?.size !== undefined && pagination.size !== null) {
      queryParams.set('page_size', pagination.size.toString());
    }

    if (filters?.DeviceId) {
      queryParams.set('doorstation_id', filters.DeviceId);
    }
    if (filters?.Status !== undefined && filters.Status !== null) {
      queryParams.set('state', filters.Status.toString());
    }
    if (filters?.Date) {
      const date = new Date(filters.Date);
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      const startTime = Math.floor(startOfDay.getTime() / 1000);
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);
      const endTime = Math.floor(endOfDay.getTime() / 1000);
      queryParams.set('start', startTime.toString());
      queryParams.set('end', endTime.toString());
    }

    const baseUrl = `/installations/${installation_id}/units/${unit_id}/calls`;
    const queryString = queryParams.toString();
    const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

    return this.callApi.get<CallEvent[]>(url);
  }

  deleteUnitUser(
    installation_id: string,
    unit_id: string,
    user_id: string
  ): Observable<boolean> {
    if (!installation_id) {
      return throwError(() => new Error("Invalid installation id"));
    }

    if (!unit_id) {
      return throwError(() => new Error("Invalid unit id"));
    }

    if (!user_id) {
      return throwError(() => new Error("Invalid user id"));
    }

    return this.delete(
      `/installations/${installation_id}/units/${unit_id}/users/${user_id}`
    );
  }

  createUnit(installation_id: string, content: any): Observable<Unit> {
    if (!installation_id)
      return throwError(() => new Error("Invalid installation id"));

    return this.post<Unit>(`/installations/${installation_id}/units`, content);
  }

  deleteUnit(installation_id: string, unit_id: string): Observable<boolean> {
    if (!installation_id)
      return throwError(() => new Error("Invalid installation id"));

    if (!unit_id) return throwError(() => new Error("Invalid unit id"));

    return this.delete(`/installations/${installation_id}/units/${unit_id}`);
  }

  updateUnit(
    installation_id: string,
    unit_id: string,
    content: any
  ): Observable<Unit> {
    if (!installation_id) {
      return throwError(() => new Error("Invalid installation id"));
    }

    if (!unit_id) {
      return throwError(() => new Error("Invalid unit id"));
    }

    return this.putWithResponse<Unit>(
      `/installations/${installation_id}/units/${unit_id}`,
      content
    );
  }

  updateUnitUserDevicesPermissions(
    installation_id: string,
    unit_id: string,
    user_id: string,
    content: any
  ): Observable<boolean> {
    if (!installation_id)
      return throwError(() => new Error("Invalid installation id"));

    if (!unit_id) return throwError(() => new Error("Invalid unit id"));

    if (!user_id) return throwError(() => new Error("Invalid user id"));

    return this.post(
      `/installations/${installation_id}/units/${unit_id}/users/${user_id}/permissions`,
      content
    ).pipe(
      map((response) => {
        return response === true;
      }),
      catchError((_) => {
        return of(false);
      })
    );
  }

  importUnits(
    installation_id: string,
    csv: string
  ): Observable<UnitImportedViewModel[]> {
    if (!installation_id)
      return throwError(() => new Error("Invalid installation id"));

    const units_to_import = { imports: csv };

    return this.post<UnitImportedViewModel[]>(
      `/installations/${installation_id}/units/import`,
      units_to_import
    );
  }

  toggleNotifications(
      installation_id: string,
      unit_id: string,
      enabled: boolean,
      device_id?: string
  ): Observable<any> {
    if (!installation_id)
      return throwError(() => new Error("Invalid installation id"));

    if (!unit_id)
      return throwError(() => new Error("Invalid unit id"));

    const body = { enabled: enabled };
    const url = `/installations/${installation_id}/units/${unit_id}${device_id ? `/devices/${device_id}` : ''}/notifications`;
    return this.post<any>(url, body);
  }
}
